import { CanDeactivateFn } from '@angular/router'
import { ConfirmationService } from 'primeng/api'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject } from 'rxjs'

/**
 * Exit protection for multi-step screens.
 *
 * Steppers deliberately don't register with `UnsavedChangesService`: that bar
 * pairs one Save with one Discard for a form whose every control is on screen,
 * and it blocks Save on whole-form validity — which a wizard cannot satisfy
 * from step 1, where every later step's required controls are mounted and
 * empty. A wizard's Save belongs in its footer, next to the step it completes.
 *
 * The bar was still carrying two things worth keeping, so they live here
 * instead: this guard (in-app navigation) and `UnloadWarningService` (closing
 * the tab). Implement both members, then list `stepperUnsavedGuard` in the
 * route's `canDeactivate`.
 */
export interface StepperUnsavedHost {
  /** Is there anything to lose right now? */
  hasUnsavedChanges(): boolean
  /** Ask before dropping it. Emits true to let the navigation through. */
  confirmLeave(): Observable<boolean>
}

export const stepperUnsavedGuard: CanDeactivateFn<StepperUnsavedHost> = component => {
  if (!component?.hasUnsavedChanges()) return true
  return component.confirmLeave()
}

/**
 * The discard prompt every stepper shows, whether the merchant clicked Cancel
 * or navigated away — one wording, one pair of buttons, one place to change it.
 *
 * Emits false for every non-accept exit (reject button, close icon, escape),
 * so dismissing the dialog keeps the merchant on the page.
 */
export const confirmDiscard = (confirmation: ConfirmationService, translate: TranslateService): Observable<boolean> => {
  const decision = new Subject<boolean>()

  const settle = (discard: boolean) => {
    decision.next(discard)
    decision.complete()
  }

  confirmation.confirm({
    message: translate.instant('common.unsavedChangesMessage'),
    header: translate.instant('common.unsavedChanges'),
    icon: 'pi pi-exclamation-triangle',
    rejectButtonProps: { label: translate.instant('common.cancel'), severity: 'secondary', outlined: true },
    acceptButtonProps: { label: translate.instant('common.discard'), severity: 'danger' },
    accept: () => settle(true),
    reject: () => settle(false),
  })

  return decision
}
